"use client";

import { useState, Fragment } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import { safeStorage, safeSessionStorage } from "../utils/storage";
import { Dialog, Menu, Transition } from "@headlessui/react";
import {
  Bars3Icon,
  Cog6ToothIcon,
  MegaphoneIcon,
  UserGroupIcon,
  HomeIcon,
  BuildingStorefrontIcon,
  XMarkIcon,
  WalletIcon,
  PlusIcon,
  CheckIcon,
  ArrowDownTrayIcon,
  NewspaperIcon,
  DocumentTextIcon,
  BellIcon,
  PuzzlePieceIcon,
  GiftIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon, CreditCardIcon } from "@heroicons/react/20/solid";
import { auth } from "../lib/firebase"; // Import auth from firebase
import { signOut } from "firebase/auth"; // Import signOut function
import { useAuth } from "../contexts/AuthContext"; // Import useAuth hook
import { useStore } from "../contexts/StoreContext";
import LanguageSelector from "./LanguageSelector";
import { useLanguage } from "../contexts/LanguageContext";
import logoVertical from "../assets/logo-vertical.png";
import logoVerticalDark from "../assets/logo-vertical-dark.png";
import { useSetupStatus } from "../hooks/useSetupStatus";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth(); // Get current user from auth context
  const {
    selectedStoreId,
    updateSelectedStore,
    stores,
    isLoading,
    selectedStore,
    canManageStore,
  } = useStore();
  const { t } = useLanguage();
  const setupCompleted = useSetupStatus();

  const handleStoreChange = (store) => {
    updateSelectedStore(store.id);
  };

  // Get display name or email
  const displayName = user?.displayName || user?.email?.split("@")[0] || "User";

  const Navigation = () => {
    const { t } = useLanguage();

    const navigation = [
      ...(canManageStore
        ? [{ name: t("nav.setup"), href: "/setup", icon: Cog6ToothIcon }]
        : []),
      { name: t("nav.dashboard"), href: "/dashboard", icon: HomeIcon },
      { name: t("nav.customers"), href: "/customers", icon: UserGroupIcon },
      { name: t("nav.passes"), href: "/passes", icon: WalletIcon },
      { name: t("nav.campaigns"), href: "/campaigns", icon: GiftIcon },
      { name: t("nav.marketing"), href: "/marketing", icon: MegaphoneIcon },
      { name: t("nav.notifications"), href: "/notifications", icon: BellIcon },
      { name: t("nav.forms"), href: "/forms", icon: DocumentTextIcon },
      { name: t("nav.flyers"), href: "/flyers", icon: NewspaperIcon },
      { name: t("nav.team"), href: "/team", icon: BuildingStorefrontIcon },
      // Club is parked: re-add the GlobeAltIcon import if this comes back.
      // { name: t("nav.club"), href: "/club", icon: GlobeAltIcon },
    ];

    // Integrations, pricing and store settings only hold admin actions; a
    // member would get a 403 from every one (see ManagerOnlyRoute).
    if (canManageStore) {
      navigation.push({
        name: t("nav.integrations"),
        href: "/integrations",
        icon: PuzzlePieceIcon,
      });
    }

    if (canManageStore && selectedStore?.current_plan != "advanced") {
      navigation.push({
        name: t("nav.pricing"),
        href: "/pricing",
        icon: CreditCardIcon,
      });
    }

    return navigation;
  };

  // Update navigation items based on current location
  const updatedNavigation = Navigation().map((item) => ({
    ...item,
    current: location.pathname.includes(item.href),
  }));

  const handleLogout = async () => {
    try {
      await signOut(auth);

      // Clear the local storage
      safeStorage.clear();
      safeSessionStorage.clear();

      navigate("/login");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  // Update the user navigation menu items
  const userNavigation = [
    { name: t("nav.profile"), href: "/profile", icon: UserCircleIcon },
    {
      name: t("nav.signOut"),
      onClick: handleLogout, // Add onClick handler
      href: null, // Remove href since we're using onClick
      icon: ArrowRightOnRectangleIcon,
    },
  ];

  const getTrialDaysRemaining = () => {
    if (!selectedStore?.trial_expires_at) return 0;

    const creationDate = new Date(selectedStore.trial_expires_at);
    const trialEndDate = new Date(creationDate.getTime());
    const today = new Date();

    const daysRemaining = Math.ceil(
      (trialEndDate - today) / (1000 * 60 * 60 * 24),
    );
    return Math.max(0, daysRemaining);
  };

  return (
    <>
      {setupCompleted && (
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50 lg:hidden"
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </Transition.Child>

            <div className="fixed inset-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                      <button
                        type="button"
                        className="-m-2.5 p-2.5"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon
                          className="h-6 w-6 text-white"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </Transition.Child>
                  {/* Sidebar component, swap this element with another sidebar if you like */}
                  <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6 pb-4 ring-1 ring-white/10">
                    <div className="flex h-16 shrink-0 items-center">
                      <img
                        src={logoVertical}
                        alt="Loyaltify"
                        className="h-12 w-auto"
                      />
                    </div>
                    <nav className="flex flex-1 flex-col">
                      <ul role="list" className="flex flex-1 flex-col gap-y-7">
                        <li>
                          <ul role="list" className="-mx-2 space-y-1">
                            {/* Update the mobile navigation mapping */}
                            {updatedNavigation.map((item) => (
                              <li key={item.name}>
                                <Link
                                  to={item.href}
                                  onClick={() => setSidebarOpen(false)}
                                  className={classNames(
                                    item.current
                                      ? "bg-gray-800 text-white"
                                      : "text-gray-400 hover:text-white hover:bg-gray-800",
                                    "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold",
                                  )}
                                >
                                  <item.icon
                                    className="h-6 w-6 shrink-0"
                                    aria-hidden="true"
                                  />
                                  {item.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>
                        <li className="mt-auto">
                          {selectedStore &&
                            selectedStore.subscription_status != "active" &&
                            selectedStore.subscription_status != "past_due" &&
                            selectedStore.subscription_status != "canceled" && (
                              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-50 border border-indigo-100">
                                <div className="w-2 self-stretch rounded-full animate-pulse bg-red-400"></div>
                                <span className="text-xs font-medium text-red-600">
                                  {getTrialDaysRemaining() > 0
                                    ? t("nav.trialDaysRemaining", {
                                        days: getTrialDaysRemaining(),
                                      })
                                    : t("nav.trialEnded")}
                                </span>
                              </div>
                            )}

                          {selectedStore?.subscription_status ===
                            "past_due" && (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-50 border border-indigo-100">
                              <div className="w-2 h-12 rounded-full animate-pulse bg-red-400"></div>
                              <span className="text-xs font-medium text-red-600">
                                {t("nav.subscriptionPastDue")}
                              </span>
                            </div>
                          )}

                          {selectedStore?.subscription_status ===
                            "canceled" && (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-50 border border-indigo-100">
                              <div className="w-2 h-12 rounded-full animate-pulse bg-red-400"></div>
                              <span className="text-xs font-medium text-red-600">
                                {t("nav.subscriptionCanceled")}
                              </span>
                            </div>
                          )}
                        </li>
                        <li>
                          <ul role="list" className="-mx-2 space-y-1">
                            <li>
                              <a
                                href="https://download.loyaltify.io"
                                target="_blank"
                                className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-400 hover:bg-gray-800 hover:text-white"
                              >
                                <ArrowDownTrayIcon
                                  className="h-6 w-6 shrink-0"
                                  aria-hidden="true"
                                />
                                {t("nav.downloadScannerApp")}
                              </a>
                            </li>
                            {canManageStore && (
                              <li>
                                <Link
                                  to="/settings"
                                  className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-400 hover:bg-gray-800 hover:text-white"
                                >
                                  <svg
                                    className="h-6 w-6"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M10.3246 4.31731C10.751 2.5609 13.249 2.5609 13.6754 4.31731C13.9508 5.45193 15.2507 5.99038 16.2478 5.38285C17.7913 4.44239 19.5576 6.2087 18.6172 7.75218C18.0096 8.74925 18.5481 10.0492 19.6827 10.3246C21.4391 10.751 21.4391 13.249 19.6827 13.6754C18.5481 13.9508 18.0096 15.2507 18.6172 16.2478C19.5576 17.7913 17.7913 19.5576 16.2478 18.6172C15.2507 18.0096 13.9508 18.5481 13.6754 19.6827C13.249 21.4391 10.751 21.4391 10.3246 19.6827C10.0492 18.5481 8.74926 18.0096 7.75219 18.6172C6.2087 19.5576 4.44239 17.7913 5.38285 16.2478C5.99038 15.2507 5.45193 13.9508 4.31731 13.6754C2.5609 13.249 2.5609 10.751 4.31731 10.3246C5.45193 10.0492 5.99037 8.74926 5.38285 7.75218C4.44239 6.2087 6.2087 4.44239 7.75219 5.38285C8.74926 5.99037 10.0492 5.45193 10.3246 4.31731Z"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                  {t("nav.storeSettings")}
                                </Link>
                              </li>
                            )}
                          </ul>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>
      )}

      {/* Static sidebar for desktop */}
      {setupCompleted && (
        <div
          className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col"
          style={{ lineHeight: "1" }}
        >
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6 pb-4">
            <div className="flex h-16 shrink-0 items-center">
              <Logo white />
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {/* Update the desktop navigation mapping */}
                    {updatedNavigation.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={classNames(
                            item.current
                              ? "bg-gray-800 text-white"
                              : "text-gray-400 hover:text-white hover:bg-gray-800",
                            "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold",
                          )}
                        >
                          <item.icon
                            className="h-6 w-6 shrink-0"
                            aria-hidden="true"
                          />
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
                <li className="mt-auto">
                  {selectedStore &&
                    selectedStore.subscription_status !== "active" &&
                    selectedStore.subscription_status !== "past_due" &&
                    selectedStore.subscription_status !== "canceled" && (
                      <Link to="/pricing" className="text-red-600">
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-50 border border-indigo-100">
                          <div className="w-2 h-12 rounded-full animate-pulse bg-red-400"></div>
                          <span className="text-xs font-medium text-red-600">
                            {getTrialDaysRemaining() > 0
                              ? t("nav.trialDaysRemaining", {
                                  days: getTrialDaysRemaining(),
                                })
                              : t("nav.trialEnded")}
                          </span>
                        </div>
                      </Link>
                    )}

                  {selectedStore &&
                    selectedStore?.subscription_status === "past_due" && (
                      <Link to="/pricing" className="text-red-600">
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-50 border border-indigo-100">
                          <div className="w-2 h-12 rounded-full animate-pulse bg-red-400"></div>
                          <span className="text-xs font-medium text-red-600">
                            {t("nav.subscriptionPastDue")}
                          </span>
                        </div>
                      </Link>
                    )}

                  {selectedStore &&
                    selectedStore?.subscription_status === "canceled" && (
                      <Link to="/pricing" className="text-red-600">
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-50 border border-indigo-100">
                          <div className="w-2 h-12 rounded-full animate-pulse bg-red-400"></div>
                          <span className="text-xs font-medium text-red-600">
                            {t("nav.subscriptionCanceled")}
                          </span>
                        </div>
                      </Link>
                    )}
                </li>
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    <li>
                      <a
                        href="https://download.loyaltify.io"
                        target="_blank"
                        className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-400 hover:bg-gray-800 hover:text-white"
                      >
                        <ArrowDownTrayIcon
                          className="h-6 w-6 shrink-0"
                          aria-hidden="true"
                        />
                        {t("nav.downloadScannerApp")}
                      </a>
                    </li>
                    {canManageStore && (
                      <li>
                        <Link
                          to="/settings"
                          className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-400 hover:bg-gray-800 hover:text-white"
                        >
                          <Cog6ToothIcon
                            aria-hidden="true"
                            className="h-6 w-6 shrink-0"
                          />
                          {t("nav.storeSettings")}
                        </Link>
                      </li>
                    )}
                  </ul>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}

      <div
        className={setupCompleted ? "lg:pl-72" : ""}
        style={{ lineHeight: "1" }}
      >
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 shadow-xs sm:px-6 lg:px-8">
          {/* Left side - Store Selector */}
          <div className="flex items-center gap-x-4">
            {setupCompleted && (
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
              >
                <span className="sr-only">Open sidebar</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </button>
            )}

            {user && (
              <>
                {!setupCompleted && (
                  <div className="flex h-16 shrink-0 items-center">
                    <img
                      src={logoVerticalDark}
                      alt="Loyaltify"
                      className="h-12 w-auto"
                    />
                  </div>
                )}
                {stores && stores.length > 1 && (
                  <Menu as="div" className="relative">
                    {isLoading ? (
                      // Loading state
                      <div className="h-8 w-32 bg-gray-200 animate-pulse rounded-md"></div>
                    ) : (
                      <>
                        <Menu.Button className="flex items-center gap-x-2 text-sm font-medium text-gray-700">
                          {selectedStore && (
                            <>
                              {selectedStore.logo_url ? (
                                <img
                                  src={selectedStore.logo_url}
                                  alt={selectedStore.name}
                                  className="h-8 w-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                  <span className="text-xs text-gray-500">
                                    {selectedStore.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <span>{selectedStore.name}</span>
                              <ChevronDownIcon
                                className="h-5 w-5 text-gray-400"
                                aria-hidden="true"
                              />
                            </>
                          )}
                        </Menu.Button>

                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute left-0 z-10 mt-2 w-56 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-hidden">
                            <div className="py-1">
                              <div className="max-h-72 overflow-y-auto">
                                {stores?.map((store) => (
                                  <Menu.Item key={store.id}>
                                    {({ active }) => (
                                      <button
                                        onClick={() => handleStoreChange(store)}
                                        className={classNames(
                                          store.id === selectedStoreId
                                            ? "bg-gray-100"
                                            : "",
                                          active ? "bg-gray-50" : "",
                                          "flex w-full items-center px-4 py-2 text-sm text-gray-700 gap-x-2",
                                        )}
                                      >
                                        {store.logo_url ? (
                                          <img
                                            src={store.logo_url}
                                            alt={store.name}
                                            className="h-6 w-6 rounded-full object-cover"
                                          />
                                        ) : (
                                          <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center">
                                            <span className="text-xs text-gray-500">
                                              {store.name
                                                .charAt(0)
                                                .toUpperCase()}
                                            </span>
                                          </div>
                                        )}
                                        {store.name}
                                        {store.id === selectedStoreId && (
                                          <CheckIcon
                                            className="ml-auto h-4 w-4 text-indigo-600"
                                            aria-hidden="true"
                                          />
                                        )}
                                      </button>
                                    )}
                                  </Menu.Item>
                                ))}
                              </div>

                              <div className="border-t border-gray-200 my-1" />

                              <Menu.Item>
                                {({ active }) => (
                                  <Link
                                    to="/stores/new"
                                    className={classNames(
                                      active ? "bg-gray-100" : "",
                                      "flex w-full items-center px-4 py-2 text-sm text-gray-700 gap-x-2",
                                    )}
                                  >
                                    <PlusIcon
                                      className="h-6 w-6 text-gray-400"
                                      aria-hidden="true"
                                    />
                                    {t("nav.createStore")}
                                  </Link>
                                )}
                              </Menu.Item>
                            </div>
                          </Menu.Items>
                        </Transition>
                      </>
                    )}
                  </Menu>
                )}
              </>
            )}
          </div>

          {/* Right side - Profile only */}
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Menu as="div" className="relative">
              <Menu.Button className="-m-1.5 flex items-center p-1.5">
                <span className="flex items-center">
                  <span className="text-sm font-semibold leading-6 text-gray-900">
                    {displayName}
                  </span>
                  <ChevronDownIcon
                    className="ml-2 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2.5 w-44 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-hidden">
                  {userNavigation.map((item) => (
                    <Menu.Item key={item.name}>
                      {({ active }) =>
                        item.href ? (
                          <Link
                            to={item.href}
                            className={classNames(
                              active ? "bg-gray-50" : "",
                              "flex items-center gap-x-2 px-3 py-1 text-sm leading-6 text-gray-900",
                            )}
                          >
                            <item.icon
                              className="h-5 w-5 shrink-0 text-gray-400"
                              aria-hidden="true"
                            />
                            {item.name}
                          </Link>
                        ) : (
                          <button
                            onClick={item.onClick}
                            className={classNames(
                              active ? "bg-gray-50" : "",
                              "flex w-full items-center gap-x-2 px-3 py-1 text-left text-sm leading-6 text-gray-900",
                            )}
                          >
                            <item.icon
                              className="h-5 w-5 shrink-0 text-gray-400"
                              aria-hidden="true"
                            />
                            {item.name}
                          </button>
                        )
                      }
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>

        <main className="py-10" id="main-content">
          <div className="px-4 sm:px-6 lg:px-8" id="main-content-inner">
            <Outlet />
          </div>
        </main>
      </div>
    </>
  );
}
